import React, { useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  VolunteerActivismOutlined as DonationIcon,
  ShoppingCartOutlined as SaleIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import {
  Share2,
  ArrowRight,
} from "lucide-react";
import moment from "moment";
import web3 from "../../../../Service/web3";
import getImageUrlByHash from "../../../../../static/logoUtils";
import AutobetLogo from "../../../../../static/Images/logo.png";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { DEFAULT_CURRENCY, DEFAULT_TOKEN_SYMBOL } from "../../../../../static/Constants";
import { CommonTable, CommonButton, CommonChip } from "../../../../Components/ui";

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const shortenAddress = (addr) => {
  if (!addr) return "";
  if (addr.length <= 10) return addr;
  return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
};

const GiftCardsTable = ({
  cards = [],
  giftcardLogo = [],
  onShare,
}) => {
  const history = useHistory();
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = (e, addr, id) => {
    e.stopPropagation();
    if (!addr) return;
    try {
      navigator.clipboard.writeText(addr);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Copy error:", err);
    }
  };

  const columns = [
    {
      id: "card",
      label: "Gift Card",
      minWidth: 180,
      render: (item) => {
        const cardId = item.giftCardId !== undefined && item.giftCardId !== null ? item.giftCardId : (item.id ?? 0);
        const imageUrl = getImageUrlByHash(item.logoHash, giftcardLogo) || AutobetLogo;

        return (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: borderRadiusPx.md,
                bgcolor: colors.slate[50],
                border: `1px solid ${colors.slate[200]}`,
                p: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt="Logo"
                sx={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = AutobetLogo;
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: colors.slate[900],
                }}
              >
                Card #{cardId}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[500],
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  maxWidth: 160,
                }}
              >
                {item.Desc || "AutoBET Gift Voucher"}
              </Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      id: "type",
      label: "Type",
      minWidth: 130,
      render: (item) => {
        const isDonation = item.isDonation === true || item.isDonation === "true" || item.isDonation === 1;
        return isDonation ? (
          <CommonChip
            label="Free Voucher"
            startIcon={<DonationIcon sx={{ fontSize: 13 }} />}
            size="small"
            sx={{
              bgcolor: colors.amber[50],
              color: colors.amber[800],
              fontWeight: 700,
              border: `1px solid ${colors.amber[200]}`,
            }}
          />
        ) : (
          <CommonChip
            label="Direct Sale"
            startIcon={<SaleIcon sx={{ fontSize: 13 }} />}
            size="small"
            sx={{
              bgcolor: colors.blue[50],
              color: colors.primary.main,
              fontWeight: 700,
              border: `1px solid ${colors.blue[200]}`,
            }}
          />
        );
      },
    },
    {
      id: "value",
      label: "Gift Value",
      minWidth: 140,
      render: (item) => {
        const tokenValue = safeFromWei(item.BETValue, "ether");
        const tokenName = item.tokenName || DEFAULT_TOKEN_SYMBOL;
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: colors.slate[900],
              fontSize: "0.95rem",
            }}
          >
            {tokenValue}{" "}
            <span style={{ color: colors.primary.main, fontWeight: 700 }}>
              {tokenName}
            </span>
          </Typography>
        );
      },
    },
    {
      id: "price",
      label: "Cost",
      minWidth: 130,
      render: (item) => {
        const isDonation = item.isDonation === true || item.isDonation === "true" || item.isDonation === 1;
        const ethPrice = safeFromWei(item.ETHPrice, "ether");
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: isDonation ? colors.emerald[700] : colors.slate[900],
            }}
          >
            {isDonation ? "FREE (+ Gas)" : `${ethPrice} ${DEFAULT_CURRENCY}`}
          </Typography>
        );
      },
    },
    {
      id: "expiry",
      label: "Expiry",
      minWidth: 160,
      render: (item) => {
        const expTimestamp = Number(item.expDate || 0);
        const isExpired = expTimestamp > 0 && expTimestamp <= moment().unix();
        const expFormatted = expTimestamp > 0 ? moment.unix(expTimestamp).format("DD MMM YYYY") : "No Expiry";
        const expRelative = expTimestamp > 0 ? moment.unix(expTimestamp).fromNow() : "";

        return (
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isExpired ? colors.rose[600] : colors.slate[800],
                fontSize: "0.85rem",
              }}
            >
              {expFormatted}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isExpired ? colors.rose[500] : colors.slate[500],
              }}
            >
              {isExpired ? "Expired" : expRelative}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "creator",
      label: "Creator",
      minWidth: 140,
      render: (item) => {
        const cardId = item.giftCardId !== undefined && item.giftCardId !== null ? item.giftCardId : (item.id ?? 0);
        const isCopied = copiedId === cardId;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                fontWeight: 700,
                color: colors.slate[700],
              }}
            >
              {shortenAddress(item.creator)}
            </Typography>
            <Tooltip title={isCopied ? "Copied!" : "Copy Creator"}>
              <IconButton
                size="small"
                onClick={(e) => handleCopy(e, item.creator, cardId)}
                sx={{ p: 0.3 }}
              >
                {isCopied ? (
                  <CheckIcon fontSize="inherit" color="success" />
                ) : (
                  <CopyIcon sx={{ fontSize: 13, color: colors.slate[400] }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      id: "actions",
      label: "Action",
      align: "right",
      minWidth: 160,
      render: (item) => {
        const cardId = item.giftCardId !== undefined && item.giftCardId !== null ? item.giftCardId : (item.id ?? 0);
        const isDonation = item.isDonation === true || item.isDonation === "true" || item.isDonation === 1;

        return (
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
            <CommonButton
              variant="primary"
              size="small"
              onClick={() => history.push(`/giftcard/${cardId}`)}
              endIcon={<ArrowRight size={14} />}
            >
              {isDonation ? "Redeem" : "Buy"}
            </CommonButton>

            {onShare && (
              <Tooltip title="Share Card Link">
                <IconButton
                  size="small"
                  onClick={() => onShare(item)}
                  sx={{
                    bgcolor: colors.slate[50],
                    border: `1px solid ${colors.slate[200]}`,
                    color: colors.slate[600],
                    "&:hover": {
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      borderColor: colors.blue[200],
                    },
                  }}
                >
                  <Share2 size={14} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
      }}
    >
      <CommonTable
        columns={columns}
        rows={cards}
        rowKey={(row, idx) =>
          row.giftCardId !== undefined && row.giftCardId !== null
            ? `giftcard-${row.giftCardId}`
            : `giftcard-idx-${idx}`
        }
        onRowClick={(row) => {
          const cardId = row.giftCardId !== undefined && row.giftCardId !== null ? row.giftCardId : (row.id ?? 0);
          history.push(`/giftcard/${cardId}`);
        }}
        containerSx={{
          borderRadius: 0,
          border: "none",
        }}
      />
    </Card>
  );
};

GiftCardsTable.propTypes = {
  cards: PropTypes.array.isRequired,
  giftcardLogo: PropTypes.array,
  onShare: PropTypes.func,
};

export default GiftCardsTable;
